import { isEqual } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import { RootState } from '..';
import {
  AnalysisStatusFilter,
  CheckStatusFilter,
  ComponentListFilters,
  DEFAULT_COMPONENT_LIST_FILTERS,
  LevelFilter,
  LinkageFilter,
} from '../slices/applicationSlice';
import {
  getAnalyzedComponentDesignGuids,
  getFailingComponentDesignGuids,
  getPassingComponentDesignGuids,
} from './componentDesignAnalysisStatusSelectors';
import { getSortedComponentDesignsForNavigationPanel } from './getSortedComponentDesignsSelectors';
import { getLinkedComponentDesignGuids, getVisibleComponentDesignGuids } from './layoutDataSelectors';
import { isLayoutViewActive } from './layoutModeSelectors';

export const getLevelFilterIsApplicable = isLayoutViewActive;

function disableInapplicableFilters(filters: ComponentListFilters, levelFilterApplicable: boolean) {
  return levelFilterApplicable || filters.level === 'off' ? filters : { ...filters, level: 'off' as const };
}

const getEffectiveDefaultComponentListFilters = createSelector(
  getLevelFilterIsApplicable,

  (levelFilterApplicable) => disableInapplicableFilters(DEFAULT_COMPONENT_LIST_FILTERS, levelFilterApplicable),
);

const getComponentListFilters = (state: RootState) => state.application.componentListFilters;

export const getEffectiveComponentListFilters = createSelector(
  getComponentListFilters,
  getLevelFilterIsApplicable,

  disableInapplicableFilters,
);

export const getComponentListFiltersAreModified = createSelector(
  getEffectiveComponentListFilters,
  getEffectiveDefaultComponentListFilters,

  (effectiveFilters, effectiveDefaults) => !isEqual(effectiveFilters, effectiveDefaults),
);

type FilterPredicate = (componentDesignGuid: string) => boolean;
const NO_FILTER: FilterPredicate = () => true;

function analysisStatusPredicate(filter: AnalysisStatusFilter, analyzedGuids: ReadonlySet<string>): FilterPredicate {
  switch (filter) {
    case 'off':
      return NO_FILTER;
    case 'analyzed':
      return (guid) => analyzedGuids.has(guid);
    case 'unanalyzed':
      return (guid) => !analyzedGuids.has(guid);
    default:
      assertExhaustiveSwitch(filter);
  }
}

function checkStatusPredicate(
  filter: CheckStatusFilter,
  passingGuids: ReadonlySet<string>,
  failingGuids: ReadonlySet<string>,
): FilterPredicate {
  switch (filter) {
    case 'off':
      return NO_FILTER;
    case 'passing':
      return (guid) => passingGuids.has(guid);
    case 'failing':
      return (guid) => failingGuids.has(guid);
    default:
      assertExhaustiveSwitch(filter);
  }
}

function levelPredicate(
  filter: LevelFilter,
  visibleGuids: ReadonlySet<string>,
  linkedGuids: ReadonlySet<string | null>,
): FilterPredicate {
  switch (filter) {
    case 'off':
      return NO_FILTER;
    case 'visibleLevels':
      return (guid) => !linkedGuids.has(guid) || visibleGuids.has(guid);
    case 'hiddenLevels':
      return (guid) => !visibleGuids.has(guid);
    default:
      assertExhaustiveSwitch(filter);
  }
}

function linkagePredicate(filter: LinkageFilter, linkedGuids: ReadonlySet<string | null>): FilterPredicate {
  switch (filter) {
    case 'off':
      return NO_FILTER;
    case 'linked':
      return (guid) => linkedGuids.has(guid);
    case 'unlinked':
      return (guid) => !linkedGuids.has(guid);
    default:
      assertExhaustiveSwitch(filter);
  }
}

export const getFilteredComponentDesignsForNavigationPanel = createSelector(
  getSortedComponentDesignsForNavigationPanel,
  getEffectiveComponentListFilters,
  getVisibleComponentDesignGuids,
  getLinkedComponentDesignGuids,
  getAnalyzedComponentDesignGuids,
  getPassingComponentDesignGuids,
  getFailingComponentDesignGuids,

  (componentDesigns, filters, visibleGuids, linkedGuids, analyzedGuids, passingGuids, failingGuids) => {
    const axisPredicates = [
      analysisStatusPredicate(filters.analysisStatus, analyzedGuids),
      checkStatusPredicate(filters.checkStatus, passingGuids, failingGuids),
      levelPredicate(filters.level, visibleGuids, linkedGuids),
      linkagePredicate(filters.linkage, linkedGuids),
    ];

    return componentDesigns.filter((design) => axisPredicates.every((matches) => matches(design.guid)));
  },
);
